import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, IconButton, Paper, Link, Tooltip } from '@mui/material';
import { UploadFile, CheckCircle, Cancel, AttachFile } from '@mui/icons-material';
import { intl } from 'dmsbasereact';
import { getTranslatedDocumentName } from '../../utils/documentTranslations';
import { getBaseURL } from '../../build_config';

const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};

const ACCEPTED_EXTENSIONS = '.pdf, .jpg, .jpeg, .png';

export const DocumentUploadSlot = ({ docName,
  file = null,
  onFileSelect,
  error = false,
  disabled = false,
  accept = null,
  existingFile = null}) => {
    const [fileError, setFileError] = useState(null);

    // Validate accept prop - ensure it's a proper MIME type object or null
    // If invalid (like document type IDs), use default
    const validAccept = useMemo(() => {
        if (!accept) return ACCEPTED_FILE_TYPES;
        // Check if accept has valid MIME type keys (should start with letters like 'application/', 'image/')
        const keys = Object.keys(accept);
        const hasInvalidKeys = keys.some(key => /^\d+$/.test(key)); // Reject numeric-only keys
        return hasInvalidKeys ? ACCEPTED_FILE_TYPES : accept;
    }, [accept]);

    const onDrop = useCallback(acceptedFiles => {
        setFileError(null);
        if (acceptedFiles.length > 0) {
            onFileSelect(docName, acceptedFiles[0]);
        }
    }, [docName, onFileSelect]);

    const onDropRejected = useCallback((rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            if (rejection.errors?.some(e => e.code === 'file-invalid-type')) {
                setFileError(intl.get('InvalidFileType').d('Invalid file type. Accepted: ') + ACCEPTED_EXTENSIONS);
            } else {
                setFileError(intl.get('FileRejected').d('File rejected'));
            }
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        multiple: false,
        disabled: disabled,
        accept: validAccept,
    });

    const handleRemove = (e) => {
        e.stopPropagation();
        onFileSelect(docName, null);
    };

    const handleExistingFileClick = (e) => {
        e.stopPropagation();
        if (existingFile?.pk) {
            const fileUrl = `${getBaseURL()}/file/download?pk=${existingFile.pk}`;
            window.open(fileUrl, '_blank');
        }
    };

    // Determine what to display: new file, existing file, or empty state
    const hasNewFile = !!file;
    const hasExistingFile = !!existingFile && !hasNewFile;
    const displayFileName = hasNewFile ? file.name : (hasExistingFile ? existingFile.original_filename : null);

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderStyle: 'dashed',
                borderColor: error ? 'error.main' : (isDragActive ? 'primary.main' : 'grey.400'),
                backgroundColor: isDragActive ? 'action.hover' : 'transparent',
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
            }}
            {...getRootProps()}
        >
            <input {...getInputProps()} />

            {hasNewFile ? (
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" overflow="hidden">
                        <CheckCircle color="success" sx={{ mr: 1, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap title={displayFileName}>
                            {displayFileName}
                        </Typography>
                    </Box>
                    <Tooltip title={intl.get("Tooltip.Action.Remove").d("Remove")}>
                        <IconButton size="small" onClick={handleRemove} sx={{ flexShrink: 0 }}>
                            <Cancel />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : hasExistingFile ? (
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" overflow="hidden">
                        <AttachFile color="primary" sx={{ mr: 1, flexShrink: 0 }} />
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleExistingFileClick}
                            sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={displayFileName}
                        >
                            {displayFileName}
                        </Link>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1, flexShrink: 0 }}>
                        {intl.get('ClickToReplace').d('Click to replace')}
                    </Typography>
                </Box>
            ) : (
                <Box display="flex" flexDirection="column" alignItems="center">
                    <UploadFile color="action" sx={{ mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                        {getTranslatedDocumentName(docName)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                        {intl.get('AcceptedFormats').d('Accepted formats')}: {ACCEPTED_EXTENSIONS}
                    </Typography>
                </Box>
            )}
            {(error || fileError) && (
                 <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {fileError || intl.get('RequiredField').d('This field is required')}
                 </Typography>
            )}
        </Paper>
    );
};

DocumentUploadSlot.propTypes = {
    docName: PropTypes.string.isRequired,
    file: PropTypes.object,
    onFileSelect: PropTypes.func.isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)), // MIME types like {'application/pdf': ['.pdf']}
    existingFile: PropTypes.object,
};

